/**
 * Construit les `FieldGroup[]` + `ViewerFile[]` consommés par l'étape de
 * vérification agence (`VerifyInfoScreen`) à partir de l'ÉTAT DU STORE
 * (`extractedFields` + `uploadedDocs`), et non du backend comme le fait
 * `CGReviewPage` (qui part de `aggregatedFields` + `extractions`).
 *
 * Différence clé : dans le store chaque champ porte UNE seule source
 * (`sourceDocType`, déjà arbitrée par `mergeExtractedFields`). On affiche donc
 * une seule pastille de provenance par champ — la liaison champ↔pièce reste
 * cliquable et fait pivoter le viewer.
 *
 * Deux champs « tarifaires » sont surfacés en plus de l'OCR :
 *  - la PLAQUE (`immatriculation`) avec repli sur `regPlate` saisi à l'étape 1
 *  - le DÉPARTEMENT (`DEPARTMENT_FIELD_KEY`), qui ne vient pas de l'OCR mais de
 *    la saisie étape 1 (`department`). Sa modification, comme celle de la
 *    plaque, doit relancer ECG Auto.
 */

import { tokens } from '../../../theme-next';
import {
  ACHETEUR_FIELDS,
  ADRESSE_FIELDS,
  COTITULAIRE_FIELDS,
  PAIRED_FIELDS,
  VEHICULE_FIELDS,
  VENDEUR_FIELDS,
  demandeImmatCoversField,
  filterFieldsByEntityKind,
  fmtValue,
  humanizeDocType,
  pieceIdentiteCotitulaireCoversField,
  type FieldDef,
} from '../../CGReview/fieldDisplayConfig';
import type { FieldEditEntry, FieldGroup } from '../../CGReview/FieldEditList';
import type { ViewerFile } from '../../CGReview/InlineFileViewer';
import type { CgDocType } from '../ocr/ocrResponseTypes';
import { natureAcheteur, natureVendeur } from '../validation/personneMorale';
import type { ExtractedFieldEntry, UploadedDocResult } from '../store/useFormCGIAStore';
import { deriveCotitulaireFlags } from './acquereurReconcile';

/** Clé synthétique du champ « Département d'immatriculation » (hors OCR). */
export const DEPARTMENT_FIELD_KEY = '__department__';
/** Clé OCR canonique de la plaque. */
export const PLATE_FIELD_KEY = 'immatriculation';

/**
 * Champs qui doivent TOUJOURS être affichés (donc éditables) à l'étape de
 * vérification, même si l'OCR ne les a pas lus (valeur vide + confiance 0).
 *
 * `date_vente` alimente `PurchaseDateTime`, colonne NOT NULL de
 * `CG_Registrations` : un dossier soumis sans elle échoue à l'INSERT. Il faut
 * donc que l'agence puisse la saisir manuellement quand l'OCR l'a ratée —
 * sinon la ligne serait masquée (cf. buildEntry) et la soumission bloquée sans
 * aucun moyen de corriger. La validation bloquante est portée par
 * VerifyInfoScreen (toast + erreur inline sur ce champ).
 */
export const ALWAYS_SHOWN_FIELD_KEYS = new Set<string>(['date_vente']);

interface BuildVerifyGroupsParams {
  extractedFields: Record<string, ExtractedFieldEntry>;
  uploadedDocs: Partial<Record<CgDocType, UploadedDocResult>>;
  /** Plaque saisie à l'étape 1 (repli si la carte grise n'a pas été lue). */
  regPlate: string;
  /** Département saisi à l'étape 1. */
  department: string;
}

export interface BuildVerifyGroupsResult {
  groups: FieldGroup[];
  viewerFiles: ViewerFile[];
}

/**
 * Aplati `uploadedDocs` en une liste de fichiers (un par URL Firebase) +
 * une map docType → index du PREMIER fichier de ce type (pour le jump viewer).
 */
const buildViewerFiles = (
  uploadedDocs: Partial<Record<CgDocType, UploadedDocResult>>,
): { viewerFiles: ViewerFile[]; docIndexByDocType: Map<string, number> } => {
  const viewerFiles: ViewerFile[] = [];
  const docIndexByDocType = new Map<string, number>();

  (Object.keys(uploadedDocs) as CgDocType[]).forEach((docType) => {
    const doc = uploadedDocs[docType];
    if (!doc) return;
    const confidencePct = doc.ocrResponse?.docTypeMatchConfidence
      ? Math.round(doc.ocrResponse.docTypeMatchConfidence * 100)
      : undefined;
    doc.firebaseUrls.forEach((url) => {
      if (!docIndexByDocType.has(docType)) docIndexByDocType.set(docType, viewerFiles.length);
      viewerFiles.push({ url, label: humanizeDocType(docType), confidence: confidencePct });
    });
  });

  return { viewerFiles, docIndexByDocType };
};

/**
 * Convertit une définition de champ en `FieldEditEntry` à partir des champs
 * extraits du store. Retourne `null` si le champ n'a jamais été extrait (pour
 * être filtré ensuite, sauf champs forcés type plaque/département).
 */
const buildEntry = (
  def: FieldDef,
  extractedFields: Record<string, ExtractedFieldEntry>,
  docIndexByDocType: Map<string, number>,
  alwaysShow = false,
  // Pastilles de référence du bloc — vide quand les pièces sont absentes.
  referenceDocs: ReferenceDocRef[] = [],
): FieldEditEntry | null => {
  const candidateKeys = [def.fieldKey, ...(def.fallbackFieldKeys ?? [])];
  const chosen = candidateKeys.map((k) => extractedFields[k]).find(Boolean) ?? null;

  const pairedKey = PAIRED_FIELDS[def.fieldKey];
  const paired = pairedKey ? extractedFields[pairedKey] : null;

  const sources =
    chosen && chosen.sourceDocType
      ? [
          {
            docTypeLabel: humanizeDocType(chosen.sourceDocType),
            docIndex: docIndexByDocType.get(chosen.sourceDocType) ?? 0,
          },
        ]
      : [];

  // Pastilles supplémentaires vers les pièces à RECOUPER, qui ne sont jamais
  // retenues comme source arbitrée du champ → sans cet ajout, aucun lien vers
  // la pièce :
  //  - bloc Acheteur : le 13750 énonce l'identité de l'acquéreur sous ses clés
  //    `titulaire_*` ;
  //  - bloc Co-titulaire : la CNI du co-titulaire sort ses champs sous les clés
  //    génériques `nom`/`prenom`/…, exclues de l'identité de l'acquéreur par
  //    EXCLUDED_FIELD_SOURCES (CY-517-VW, DM-724-VQ).
  // Dédupliqué au cas où la pièce serait DÉJÀ la source arbitrée du champ.
  referenceDocs.forEach((ref) => {
    if (!ref.coversField(ref.rawFields, def.fieldKey)) return;
    if (sources.some((s) => s.docIndex === ref.docIndex)) return;
    sources.push({ docTypeLabel: ref.docTypeLabel, docIndex: ref.docIndex });
  });

  const value = chosen ? fmtValue(chosen.field.value) : '';
  const confidence = chosen ? chosen.field.confidence : 0;

  // Champ jamais extrait ET vide → on le masque (sinon liste polluée de « — »),
  // SAUF :
  //  - les champs obligatoires toujours affichés (ex : date_vente) que l'agence
  //    doit pouvoir saisir même quand l'OCR ne les a pas lus ;
  //  - les groupes FIXES (`alwaysShow`) : retour Biwiz 2026-07 — quand l'IA ne
  //    lit qu'une partie d'un bloc (ex : seule la ville d'une adresse), les
  //    autres champs du bloc ne doivent PAS disparaître. On garde le champ vide
  //    (éditable) avec un indicateur discret « non reconnu » côté UI.
  if (value === '' && confidence === 0 && !alwaysShow && !ALWAYS_SHOWN_FIELD_KEYS.has(def.fieldKey)) return null;

  return {
    fieldKey: def.fieldKey,
    label: def.label,
    value,
    confidence,
    pairedFieldKey: pairedKey,
    pairedValue: paired ? fmtValue(paired.field.value) : '',
    pairedConfidence: paired ? paired.field.confidence : 0,
    sources,
  };
};

/**
 * Référence cliquable vers une pièce du dossier qui RECOUPE un champ sans en
 * être la source arbitrée (label + index viewer + OCR brut). Purement additif :
 * ne participe jamais au choix de la valeur affichée.
 */
interface ReferenceDocRef {
  docTypeLabel: string;
  docIndex: number;
  rawFields: unknown;
  coversField: (rawFields: unknown, fieldKey: string) => boolean;
}

const buildGroupFromStore = (
  title: string,
  accentColor: string,
  defs: FieldDef[],
  extractedFields: Record<string, ExtractedFieldEntry>,
  docIndexByDocType: Map<string, number>,
  // Groupes fixes : garder tous les champs affichés même non reconnus par l'IA
  // (retour Biwiz — ne plus faire disparaître les champs d'un bloc partiel).
  alwaysShow = false,
  referenceDocs: ReferenceDocRef[] = [],
): FieldGroup => ({
  title,
  accentColor,
  fields: defs
    .map((def) => buildEntry(def, extractedFields, docIndexByDocType, alwaysShow, referenceDocs))
    .filter((f): f is FieldEditEntry => f !== null),
});

export const buildVerifyGroups = ({
  extractedFields,
  uploadedDocs,
  regPlate,
  department,
}: BuildVerifyGroupsParams): BuildVerifyGroupsResult => {
  const { viewerFiles, docIndexByDocType } = buildViewerFiles(uploadedDocs);

  const vehicule = buildGroupFromStore(
    'Véhicule',
    tokens.color.yellow,
    VEHICULE_FIELDS,
    extractedFields,
    docIndexByDocType,
    true,
  );

  // La plaque doit TOUJOURS être éditable (champ tarifaire) : si la carte grise
  // n'a pas été lue, on retombe sur la valeur saisie à l'étape 1 (regPlate).
  // Avec `alwaysShow`, l'entrée plaque est toujours présente (mais éventuellement
  // vide) : on ne teste donc plus sa présence mais sa VALEUR, et on remplit le
  // repli regPlate en place plutôt que d'en préfixer une seconde.
  const vehiculeFields: FieldEditEntry[] = [...vehicule.fields];
  const plateFieldIdx = vehiculeFields.findIndex((f) => f.fieldKey === PLATE_FIELD_KEY);
  if (plateFieldIdx === -1) {
    vehiculeFields.unshift({
      fieldKey: PLATE_FIELD_KEY,
      label: 'Immatriculation',
      value: regPlate,
      confidence: 0,
      sources: [],
    });
  } else if (vehiculeFields[plateFieldIdx].value === '') {
    vehiculeFields[plateFieldIdx] = { ...vehiculeFields[plateFieldIdx], value: regPlate };
  }

  // Champ Département (hors OCR) inséré juste après la plaque — les deux
  // entrées tarifaires sont ainsi groupées en tête de la fiche véhicule.
  const departmentEntry: FieldEditEntry = {
    fieldKey: DEPARTMENT_FIELD_KEY,
    label: "Département d'immatriculation",
    value: department,
    confidence: 0,
    sources: [],
  };
  const plateIdx = vehiculeFields.findIndex((f) => f.fieldKey === PLATE_FIELD_KEY);
  const insertAt = plateIdx === -1 ? 0 : plateIdx + 1;
  vehiculeFields.splice(insertAt, 0, departmentEntry);

  // Personne morale vs particulier : n'afficher que les champs pertinents (raison
  // sociale + SIRET pour une société, civilité/nom/prénom pour un particulier).
  // RÈGLE UNIQUE `natureVendeur` / `natureAcheteur` — la même que la review god
  // (CGReviewPage) et que les pièces réclamées (buildEffectiveDocQueue). Avant
  // l'unification, cet écran ne lisait QUE `*_est_personne_morale` : un vendeur
  // dont la cession déclarait « particulier » mais dont le SIRET avait été lu
  // n'exposait ni raison sociale ni SIRET, alors que c'est `seller_client_type`
  // (déduit des identifiants, pas du flag) qui partait en base. L'agence ne
  // pouvait donc pas corriger ce qu'elle envoyait (GP-482-JC).
  const valeurChamp = (key: string): unknown => extractedFields[key]?.field?.value;
  const vendeurNature = natureVendeur({
    flagCession: valeurChamp('vendeur_est_personne_morale'),
    hasKbisVendeur: Boolean(uploadedDocs.kbis_vendeur),
    vendeurSiret: valeurChamp('vendeur_siret'),
    vendeurRaisonSociale: valeurChamp('vendeur_raison_sociale'),
    titulaireSiren: valeurChamp('titulaire_siren'),
  });
  const acheteurNature = natureAcheteur({
    flagCession: valeurChamp('acheteur_est_personne_morale'),
    // `kbis` non routé = dossier antérieur au dédoublement des slots, traité
    // comme acquéreur par convention historique. Un `kbis_vendeur` ne compte
    // surtout pas ici.
    hasKbis: Boolean(uploadedDocs.kbis_acheteur) || Boolean(uploadedDocs.kbis),
    acheteurSiret: valeurChamp('acheteur_siret'),
    acheteurRaisonSociale: valeurChamp('acheteur_raison_sociale'),
  });

  // Co-titulaire : le groupe de champs est affiché dès qu'un co-titulaire
  // ACHETEUR est détecté sur le Cerfa 13750 OU déclaré à la main par l'agence.
  // On NE l'affiche pas par défaut : des champs vides sur chaque dossier
  // embrouilleraient les agences (décision Luc).
  //
  // ⚠️ On délègue à `deriveCotitulaireFlags` — la MÊME source que la checklist
  // Documents et que /review. L'ancien `readIsPM('cobuyer_present')` lisait une
  // clé de FORM VALUES qui n'existe PAS dans `extractedFields` (la clé OCR est
  // `cotitulaire_present`, `cobuyer_present` n'étant produit qu'au mapping
  // formulaire) : le bloc restait donc invisible même quand le 13750 déclarait
  // un co-titulaire — cas EA-035-SV / reg 11910, co-titulaire LEGRAND MELANIE
  // lu à 95 % et pourtant aucun encart affiché (retour Luc 2026-07-30).
  const cotitulaireActif = deriveCotitulaireFlags(extractedFields, uploadedDocs).acheteurActif;

  // Référence vers le Cerfa 13750 pour les champs du bloc Acheteur (cf.
  // DEMANDE_IMMAT_ACHETEUR_FIELD_KEYS). `null` si la pièce n'est pas au dossier.
  const demandeImmatIdx = docIndexByDocType.get('cerfa_demande_immat');
  const demandeImmatRefs: ReferenceDocRef[] =
    demandeImmatIdx === undefined
      ? []
      : [
          {
            docTypeLabel: humanizeDocType('cerfa_demande_immat'),
            docIndex: demandeImmatIdx,
            rawFields: uploadedDocs.cerfa_demande_immat?.ocrResponse?.fields,
            coversField: demandeImmatCoversField,
          },
        ];

  // Référence vers la CNI du co-titulaire pour les champs du bloc Co-titulaire
  // (cf. PIECE_IDENTITE_COTITULAIRE_FIELD_KEYS). Le store remappe déjà ses
  // champs en `cotitulaire_*`, donc la pastille peut y être la source arbitrée ;
  // la garde de déduplication ci-dessus évite alors le doublon. Elle reste utile
  // quand c'est le 13750 qui remporte l'arbitrage du champ.
  // RECTO uniquement : le verso ne porte ni nom ni prénom (skill CTVO §13.14).
  const piCotitulaireIdx = docIndexByDocType.get('piece_identite_cotitulaire');
  const piCotitulaireRefs: ReferenceDocRef[] =
    piCotitulaireIdx === undefined
      ? []
      : [
          {
            docTypeLabel: humanizeDocType('piece_identite_cotitulaire'),
            docIndex: piCotitulaireIdx,
            rawFields: uploadedDocs.piece_identite_cotitulaire?.ocrResponse?.fields,
            coversField: pieceIdentiteCotitulaireCoversField,
          },
        ];

  const groups: FieldGroup[] = [
    { ...vehicule, fields: vehiculeFields },
    buildGroupFromStore(
      'Vendeur',
      tokens.color.textMuted,
      filterFieldsByEntityKind(VENDEUR_FIELDS, vendeurNature),
      extractedFields,
      docIndexByDocType,
      true,
    ),
    buildGroupFromStore(
      'Acheteur',
      tokens.color.blue,
      filterFieldsByEntityKind(ACHETEUR_FIELDS, acheteurNature),
      extractedFields,
      docIndexByDocType,
      true,
      demandeImmatRefs,
    ),
    buildGroupFromStore('Adresse acheteur', tokens.color.blue, ADRESSE_FIELDS, extractedFields, docIndexByDocType, true),
    ...(cotitulaireActif
      ? [
          buildGroupFromStore(
            'Co-titulaire',
            tokens.color.purple,
            COTITULAIRE_FIELDS,
            extractedFields,
            docIndexByDocType,
            true,
            piCotitulaireRefs,
          ),
        ]
      : []),
  ];

  return { groups, viewerFiles };
};
